import React from "react";
import { Box, Typography, Button } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { AppColors } from "../../../../../util/AppColors";

const MessageBubble = ({ message }) => {
  const isDoctor = message.sender === "doctor";
  const isSystem = message.sender === "system";

  // 1. System/Reminder Message
  if (isSystem && message.type === "reminder") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          my: 3,
          width: "100%"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#EFF6FC",
            border: "1px solid #79C5EF60",
            borderRadius: "12px",
            px: 3,
            py: 1.5,
            width: "80%",
            maxWidth: "480px",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.02)"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "#D0E7FC",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#0054A6"
              }}
            >
              <NotificationsActiveIcon sx={{ fontSize: "20px" }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#0054A6",
                  fontFamily: "Montserrat"
                }}
              >
                Reminder set
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#475569",
                  fontFamily: "Montserrat"
                }}
              >
                {message.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#64748B",
                  fontFamily: "Montserrat",
                  mt: 0.25
                }}
              >
                {message.time}
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            variant="text"
            sx={{
              textTransform: "none",
              fontWeight: "600",
              color: "#0054A6",
              fontFamily: "Montserrat",
              fontSize: "12px",
              "&:hover": { backgroundColor: "transparent", opacity: 0.8 }
            }}
          >
            View
          </Button>
        </Box>
      </Box>
    );
  }

  // Image/Photo Message
  if (message.type === "image") {
    const imageSrc = message.content;
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: isDoctor ? "flex-end" : "flex-start",
          mb: 2.5,
          width: "100%"
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "92%", width: "min(100%, 520px)" }}>
          <Box
            sx={{
              backgroundColor: isDoctor ? "#EAF8E6" : "#FFF",
              border: isDoctor ? "none" : "1px solid #E2E8F0",
              borderRadius: isDoctor ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              p: 0.75,
              boxShadow: "0px 1px 2px rgba(0,0,0,0.03)",
              overflow: "hidden"
            }}
          >
            <Box
              component="a"
              href={imageSrc || undefined}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "block", lineHeight: 0, cursor: imageSrc ? "zoom-in" : "default" }}
            >
              <img
                src={imageSrc}
                alt={message.fileName || "Shared image"}
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  height: "auto",
                  maxHeight: "420px",
                  borderRadius: "12px",
                  objectFit: "contain",
                  display: "block",
                  backgroundColor: "#F1F5F9"
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isDoctor ? "flex-end" : "flex-start",
              mt: 0.5,
              px: 1,
              gap: 0.5
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                color: "#94A3B8",
                fontFamily: "Montserrat"
              }}
            >
              {message.timestamp}
            </Typography>
            {isDoctor && (
              <DoneAllIcon
                sx={{
                  fontSize: "14px",
                  color: message.status === "read" ? "#3B82F6" : "#94A3B8"
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // 2. Document/PDF Message
  if (message.type === "document") {
    const fileLabel =
      message.fileName ||
      (typeof message.content === "string"
        ? message.content.split("/").pop()
        : "") ||
      "Attachment";
    const fileHref =
      message.content && /^https?:\/\//i.test(String(message.content))
        ? message.content
        : null;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: isDoctor ? "flex-end" : "flex-start",
          mb: 2.5,
          width: "100%"
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "92%", width: "min(100%, 420px)" }}>
          <Box
            component={fileHref ? "a" : "div"}
            href={fileHref || undefined}
            target={fileHref ? "_blank" : undefined}
            rel={fileHref ? "noopener noreferrer" : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
              backgroundColor: isDoctor ? "#EAF8E6" : "#FFF",
              border: isDoctor ? "none" : "1px solid #E2E8F0",
              borderRadius: isDoctor ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              p: 2,
              boxShadow: "0px 1px 2px rgba(0,0,0,0.05)",
              textDecoration: "none",
              color: "inherit",
              cursor: fileHref ? "pointer" : "default"
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                backgroundColor: "#FEE2E2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#EF4444",
                flexShrink: 0
              }}
            >
              <InsertDriveFileIcon sx={{ fontSize: "24px" }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#1E293B",
                  fontFamily: "Montserrat"
                }}
              >
                {fileLabel}
              </Typography>
              {message.fileSize ? (
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: AppColors.gray5,
                    fontFamily: "Montserrat",
                    mt: 0.25
                  }}
                >
                  {message.fileSize}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isDoctor ? "flex-end" : "flex-start",
              mt: 0.5,
              px: 1,
              gap: 0.5
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                color: "#94A3B8",
                fontFamily: "Montserrat"
              }}
            >
              {message.timestamp}
            </Typography>
            {isDoctor && (
              <DoneAllIcon
                sx={{
                  fontSize: "14px",
                  color: message.status === "read" ? "#3B82F6" : "#94A3B8"
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // 3. Text Message (Incoming/Outgoing)
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isDoctor ? "flex-end" : "flex-start",
        mb: 2.5,
        width: "100%"
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "92%" }}>
        <Box
          sx={{
            backgroundColor: isDoctor ? "#EAF8E6" : "#FFF",
            color: "#1E293B",
            border: isDoctor ? "none" : "1px solid #E2E8F0",
            borderRadius: isDoctor ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
            px: 2.5,
            py: 1.5,
            boxShadow: "0px 1px 2px rgba(0,0,0,0.03)"
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              lineHeight: 1.6,
              fontFamily: "Montserrat",
              fontWeight: "500",
              whiteSpace: "pre-line"
            }}
          >
            {message.content}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isDoctor ? "flex-end" : "flex-start",
            mt: 0.5,
            px: 1,
            gap: 0.5
          }}
        >
          <Typography
            sx={{
              fontSize: "10px",
              color: "#94A3B8",
              fontFamily: "Montserrat"
            }}
          >
            {message.timestamp}
          </Typography>
          {isDoctor && (
            <DoneAllIcon
              sx={{
                fontSize: "14px",
                color: message.status === "read" ? "#3B82F6" : "#94A3B8"
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MessageBubble;
